import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismBanner, PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { eq } from 'ember-truth-helpers';
import type { FileWithValidation } from 'lumen/components/general/file-drop-zone';
import GeneralFileDropZone from 'lumen/components/general/file-drop-zone';
import type { SubscriptionProfile } from 'lumen/models';
import type { FileType } from 'lumen/utils/constants/file-types';

import styles from './logo-field.module.css';

type ValidationError = FileWithValidation['validationErrors'][number];

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB per Figma spec

interface SettingsMyHotelLogoFieldSignature {
  Args: {
    disabled?: boolean;
    onCancelPendingFile: () => void;
    onFileSelect: (file: File) => void;
    onStagePendingDelete: () => void;
    pendingChange: 'upload' | 'delete' | null;
    pendingFileName: string;
    profile: SubscriptionProfile | null | undefined;
  };
}

export default class SettingsMyHotelLogoField extends Component<SettingsMyHotelLogoFieldSignature> {
  @service declare intl: IntlService;

  @tracked validationError: ValidationError | null = null;

  allowedFormats: FileType[] = ['png', 'jpeg', 'gif'];
  maxFileSize = MAX_FILE_SIZE;

  get fileName(): string {
    const path = this.args.profile?.logoPath ?? '';
    return path.split('/').pop() ?? path;
  }

  get showExistingLogo(): boolean {
    return (
      !!this.args.profile?.logoPath && this.args.pendingChange !== 'delete'
    );
  }

  get validationErrorMessage(): string | null {
    if (this.validationError === 'FileTooLarge') {
      return this.intl.t(
        'settings.my_hotel.hotel_details.logo_error_too_large',
      );
    }
    if (this.validationError === 'InvalidFileType') {
      return this.intl.t(
        'settings.my_hotel.hotel_details.logo_error_invalid_type',
      );
    }
    return null;
  }

  @action handleFileDrop(
    filesWithValidation: FileWithValidation[],
    isTooManyFiles: boolean,
  ): void {
    if (isTooManyFiles) {
      return;
    }

    const [droppedFile] = filesWithValidation;
    if (!droppedFile) {
      return;
    }

    const firstError = droppedFile.validationErrors[0];
    if (firstError) {
      this.validationError = firstError;
      return;
    }

    this.validationError = null;
    this.args.onFileSelect(droppedFile.file);
  }

  <template>
    <div class="flex w-full flex-col gap-200" data-test-id="logo-field">
      <label class="text-200--bold">
        {{t "settings.my_hotel.hotel_details.logo"}}
      </label>

      {{#if this.showExistingLogo}}
        {{#if (eq @pendingChange "upload")}}
          <PrismBanner
            @bannerMessage={{t "settings.my_hotel.hotel_details.logo_only_one"}}
            @bannerStyle="info"
            @icon="circle-information-outline"
            data-test-id="logo-banner"
          />
        {{/if}}
        <div
          class="bg-neutral-subdued rounded-100 flex items-center gap-200 px-400 py-300"
          data-test-id="logo-file-item"
        >
          <PrismIcon @color="success" @name="circle-checkmark" />
          <span
            class="heading-4 flex-1 truncate"
            data-test-id="logo-filename"
          >{{this.fileName}}</span>
          <PrismButton
            @buttonType="ghost"
            @icon="bin"
            @isDisabled={{@disabled}}
            @onClick={{@onStagePendingDelete}}
            aria-label={{t "general.remove"}}
            data-test-id="logo-remove"
          />
        </div>
      {{else if (eq @pendingChange "upload")}}
        <PrismBanner
          @bannerMessage={{t "settings.my_hotel.hotel_details.logo_only_one"}}
          @bannerStyle="info"
          @icon="circle-information-outline"
          data-test-id="logo-banner"
        />
        <div
          class="bg-neutral-subdued rounded-100 flex items-center gap-200 px-400 py-300"
          data-test-id="logo-pending-item"
        >
          <PrismIcon @name="upload" />
          <span
            class="heading-4 flex-1 truncate"
            data-test-id="logo-pending-filename"
          >{{@pendingFileName}}</span>
          <PrismButton
            @buttonType="ghost"
            @icon="bin"
            @isDisabled={{@disabled}}
            @onClick={{@onCancelPendingFile}}
            aria-label={{t "general.remove"}}
            data-test-id="logo-cancel-pending"
          />
        </div>
      {{else}}
        {{#if this.validationErrorMessage}}
          <PrismBanner
            @bannerMessage={{this.validationErrorMessage}}
            @bannerStyle="critical"
            @icon="warning"
            data-test-id="logo-validation-error"
          />
        {{/if}}
        {{! No dedicated disabled state exists in design yet. opacity-50 + pointer-events-none
            is a temporary solution until a proper disabled state is designed. }}
        <div
          class="{{styles.hotel-logo-dropzone}}
            {{if @disabled 'pointer-events-none opacity-50'}}"
        >
          <GeneralFileDropZone
            @allowedFormats={{this.allowedFormats}}
            @isDisabled={{@disabled}}
            @maxFileSize={{this.maxFileSize}}
            @onFileDrop={{this.handleFileDrop}}
            class={{styles.dropzone}}
            data-test-id="logo-dropzone"
          />
        </div>
      {{/if}}
    </div>
  </template>
}
